import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Card, 
  message, 
  Select, 
  DatePicker,
  Row,
  Col,
  Avatar,
  Tooltip
} from 'antd';
import { SearchOutlined, EyeOutlined, ReloadOutlined, ShopOutlined, EyeInvisibleOutlined, WarningOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';
import '../Users/Users.css';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title } = Typography;
const { RangePicker } = DatePicker;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }

  /* Fix pagination text */
  .ant-pagination {
    color: ${COLORS.textSecondary};

    .ant-pagination-item {
      background-color: ${COLORS.bgSecondary};
      border-color: ${COLORS.border};

      a {
        color: ${COLORS.textPrimary};
      }

      &.ant-pagination-item-active {
        background-color: ${COLORS.accent};
        border-color: ${COLORS.accent};

        a {
          color: ${COLORS.bgPrimary};
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      background-color: ${COLORS.bgSecondary};
      border-color: ${COLORS.border};

      .ant-pagination-item-link {
        color: ${COLORS.textPrimary};
        background-color: ${COLORS.bgSecondary};
        border-color: ${COLORS.border};
      }
    }

    .ant-pagination-options {
      .ant-select-selector {
        background-color: ${COLORS.bgSecondary} !important;
        color: ${COLORS.textPrimary} !important;
        border-color: ${COLORS.border} !important;
      }
      .ant-select-arrow {
        color: ${COLORS.textSecondary} !important;
      }
    }

    .ant-pagination-total-text {
      color: ${COLORS.textSecondary};
    }
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
`;

const FilterRow = styled(Row)`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.span`
  color: ${COLORS.textSecondary};
  margin-right: 8px;
  display: inline-block;
  min-width: 120px;
`;

interface WalletSummary {
  totalWallets: number;
  notUnderWatchCount: number;
  nonActiveStatusCount: number;
  deletedWalletStatusCount: number;
  watchedCount: number;
}

const Wallets: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState<any[]>([]);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [summary, setSummary] = useState<WalletSummary | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Filter states
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [merchantFilter, setMerchantFilter] = useState<string>(searchParams.get('merchantCompanyId') || '');
  const [memberIdFilter, setMemberIdFilter] = useState<string>(searchParams.get('externalMemberId') || '');
  const [walletStatusFilter, setWalletStatusFilter] = useState<string>(searchParams.get('walletStatus') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [hasMemberFilter, setHasMemberFilter] = useState<string>(searchParams.get('hasMember') || '');
  const [notionalBalanceFilter, setNotionalBalanceFilter] = useState<string>(searchParams.get('notionalBalance') || '');
  const [isUnderWatchFilter, setIsUnderWatchFilter] = useState<string>(searchParams.get('isUnderWatch') || '');
  const [createdDateRange, setCreatedDateRange] = useState<any>(null);
  const [updatedDateRange, setUpdatedDateRange] = useState<any>(null);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  // Load merchants for filter dropdown
  useEffect(() => {
    fetchMerchants();
  }, []);

  // Parse date ranges from URL params
  useEffect(() => {
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    if (dateFrom && dateTo) {
      setCreatedDateRange([dayjs(dateFrom), dayjs(dateTo)]);
    }

    const updatedFrom = searchParams.get('updatedFrom');
    const updatedTo = searchParams.get('updatedTo');
    if (updatedFrom && updatedTo) {
      setUpdatedDateRange([dayjs(updatedFrom), dayjs(updatedTo)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, search, merchantFilter, memberIdFilter, walletStatusFilter, statusFilter, hasMemberFilter, notionalBalanceFilter, isUnderWatchFilter, createdDateRange, updatedDateRange]);

  const fetchMerchants = async () => {
    try {
      setLoadingMerchants(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WALLET_MERCHANTS || '/api/Administrator/wallets/merchants');
      if (response.data?.status === 'success' && response.data?.data) {
        setMerchants(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoadingMerchants(false);
    }
  };

  const fetchWallets = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
        sort: 'DESC',
      };

      if (search) {
        params.search = search;
      }
      if (merchantFilter) {
        params.merchantCompanyId = merchantFilter;
      }
      if (memberIdFilter) {
        params.externalMemberId = memberIdFilter;
      }
      if (walletStatusFilter) {
        params.walletStatus = walletStatusFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (hasMemberFilter) {
        params.hasMember = hasMemberFilter;
      }
      if (notionalBalanceFilter) {
        params.notionalBalance = notionalBalanceFilter;
      }
      if (isUnderWatchFilter) {
        params.isUnderWatch = isUnderWatchFilter;
      }
      if (createdDateRange && createdDateRange[0] && createdDateRange[1]) {
        params.dateFrom = createdDateRange[0].format('YYYY-MM-DD');
        params.dateTo = createdDateRange[1].format('YYYY-MM-DD');
      }
      if (updatedDateRange && updatedDateRange[0] && updatedDateRange[1]) {
        params.updatedFrom = updatedDateRange[0].format('YYYY-MM-DD');
        params.updatedTo = updatedDateRange[1].format('YYYY-MM-DD');
      }

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WALLETS || '/api/Administrator/wallets', { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setWallets(data.itemsList || []);
        const paginator = data.paginator;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total: paginator.itemCount || 0,
          });
        }
        // Set summary stats
        if (data.summary) {
          setSummary(data.summary);
        }
      }
    } catch (error: any) {
      console.error('Error fetching wallets:', error);
      message.error(error.response?.data?.message || 'Failed to fetch wallets');
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    newParams.set('pageId', '1'); // Reset to first page when filters change
    setSearchParams(newParams);
  };

  const handleFilterChange = (key: string, value: string | null) => {
    updateURLParams({ [key]: value });
  };

  const handleDateRangeChange = (key: string, dates: any) => {
    if (key === 'created') {
      setCreatedDateRange(dates);
      updateURLParams({
        dateFrom: dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
        dateTo: dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      });
    } else if (key === 'updated') {
      setUpdatedDateRange(dates);
      updateURLParams({
        updatedFrom: dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
        updatedTo: dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
      });
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setMerchantFilter('');
    setMemberIdFilter('');
    setWalletStatusFilter('');
    setStatusFilter('');
    setHasMemberFilter('');
    setNotionalBalanceFilter('');
    setIsUnderWatchFilter('');
    setCreatedDateRange(null);
    setUpdatedDateRange(null);
    setSearchParams(new URLSearchParams({ pageId: '1', perPage: '10' }));
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: 'Wallet ID',
      dataIndex: 'walletId',
      key: 'walletId',
      render: (id: string) => id ? (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {id.substring(0, 16)}...
        </span>
      ) : '-',
    },
    {
      title: 'EVM Address',
      dataIndex: 'evmAddress',
      key: 'evmAddress',
      render: (address: string) => address ? (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {address.substring(0, 10)}...{address.substring(address.length - 8)}
        </span>
      ) : '-',
    },
    {
      title: 'Bitcoin Address',
      dataIndex: 'bitcoinAddress',
      key: 'bitcoinAddress',
      render: (address: string) => address ? (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {address.substring(0, 10)}...{address.substring(address.length - 8)}
        </span>
      ) : '-',
    },
    {
      title: 'Solana Address',
      dataIndex: 'solanaAddress',
      key: 'solanaAddress',
      render: (address: string) => address ? (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {address.substring(0, 10)}...{address.substring(address.length - 8)}
        </span>
      ) : '-',
    },
    {
      title: 'TRON Address',
      dataIndex: 'tronAddress',
      key: 'tronAddress',
      render: (address: string) => address ? (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {address.substring(0, 10)}...{address.substring(address.length - 8)}
        </span>
      ) : '-',
    },
    {
      title: 'Company',
      key: 'company',
      render: (_: any, record: any) => {
        if (record.company?.id) {
          return (
            <Tooltip title={record.company.name || 'Company'}>
              <Link
                to={`/kivu/companies/${record.company.id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <Avatar
                  src={record.company.logoUrl}
                  icon={<ShopOutlined />}
                  size={32}
                  style={{ 
                    backgroundColor: COLORS.bgSecondary, 
                    color: COLORS.textPrimary, 
                    border: `1px solid ${COLORS.border}`,
                    cursor: 'pointer'
                  }}
                />
              </Link>
            </Tooltip>
          );
        }
        return <Avatar icon={<ShopOutlined />} size={32} style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }} />;
      },
    },
    {
      title: 'Member Name/Email',
      key: 'memberInfo',
      render: (_: any, record: any) => {
        const display = record.externalMemberName || record.externalMemberEmail || '-';
        if (record.externalMemberId) {
          return <>{display} <span style={{ fontSize: '11px', color: COLORS.textSecondary, fontFamily: 'monospace' }}>[{record.externalMemberId}]</span></>;
        }
        return display;
      },
    },
    {
      title: 'Wallet Status',
      dataIndex: 'walletStatus',
      key: 'walletStatus',
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          ACTIVE: 'green',
          PENDING: 'orange',
          DELETED: 'red',
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Model Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          ACTIVE: 'green',
          PENDING: 'orange',
          DELETED: 'red',
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Notional Balance',
      dataIndex: 'notionalBalance',
      key: 'notionalBalance',
      render: (balance: number) => {
        const numBalance = Number(balance || 0);
        const color = numBalance < 0 ? '#F87171' : numBalance > 0 ? '#10B981' : COLORS.textPrimary;
        return (
          <span style={{ fontFamily: 'monospace', color, fontWeight: 500 }}>
            {numBalance.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 8,
            })}
          </span>
        );
      },
    },
    {
      title: 'Under Watch',
      dataIndex: 'isUnderWatch',
      key: 'isUnderWatch',
      render: (isUnderWatch: boolean) => (
        <Tag color={isUnderWatch ? 'green' : 'default'}>
          {isUnderWatch ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 150,
      render: (_: any, record: any) => (
        <Link
          to={`/kivu/wallets/${record.id}`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/kivu/wallets/${record.id}`);
          }}
        >
          <Button
            icon={<EyeOutlined />}
            size="small"
            type="default"
            style={{
              backgroundColor: 'transparent',
              borderColor: COLORS.border,
              color: COLORS.textPrimary,
            }}
          >
            View
          </Button>
        </Link>
      ),
    },
  ];

  // Quick filter handlers
  const handleQuickFilterNotWatched = () => {
    setIsUnderWatchFilter('false');
    handleFilterChange('isUnderWatch', 'false');
  };

  const handleQuickFilterNonActive = () => {
    setStatusFilter('DELETED');
    handleFilterChange('status', 'DELETED');
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Multi Party Wallets
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchWallets}
          loading={loading}
        >
          Refresh
        </Button>
      </HeaderContainer>

      {/* Wallet Monitoring Summary */}
      {summary && (
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
            marginBottom: 24,
          }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>Total Wallets</div>
                <div style={{ color: COLORS.textPrimary, fontSize: 20, fontWeight: 600 }}>
                  {summary.totalWallets.toLocaleString()}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>Under Watch</div>
                <div style={{ color: '#10B981', fontSize: 20, fontWeight: 600 }}>
                  {summary.watchedCount.toLocaleString()}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Tooltip title="Click to filter wallets not being monitored by OnchainWatcher">
                <Button
                  type="text"
                  onClick={handleQuickFilterNotWatched}
                  style={{ 
                    padding: 8, 
                    height: 'auto', 
                    width: '100%',
                    background: summary.notUnderWatchCount > 0 ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
                    border: summary.notUnderWatchCount > 0 ? '1px solid rgba(251, 191, 36, 0.3)' : 'none',
                    borderRadius: 8,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                      <EyeInvisibleOutlined style={{ marginRight: 4 }} />
                      Not Watched
                    </div>
                    <div style={{ color: summary.notUnderWatchCount > 0 ? '#FBBF24' : COLORS.textSecondary, fontSize: 20, fontWeight: 600 }}>
                      {summary.notUnderWatchCount.toLocaleString()}
                    </div>
                  </div>
                </Button>
              </Tooltip>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Tooltip title="Click to filter wallets with non-active model status">
                <Button
                  type="text"
                  onClick={handleQuickFilterNonActive}
                  style={{ 
                    padding: 8, 
                    height: 'auto', 
                    width: '100%',
                    background: summary.nonActiveStatusCount > 0 ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                    border: summary.nonActiveStatusCount > 0 ? '1px solid rgba(239, 68, 68, 0.3)' : 'none',
                    borderRadius: 8,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                      <WarningOutlined style={{ marginRight: 4 }} />
                      Non-Active Status
                    </div>
                    <div style={{ color: summary.nonActiveStatusCount > 0 ? '#EF4444' : COLORS.textSecondary, fontSize: 20, fontWeight: 600 }}>
                      {summary.nonActiveStatusCount.toLocaleString()}
                    </div>
                  </div>
                </Button>
              </Tooltip>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>Deleted Wallets</div>
                <div style={{ color: summary.deletedWalletStatusCount > 0 ? '#EF4444' : COLORS.textSecondary, fontSize: 20, fontWeight: 600 }}>
                  {summary.deletedWalletStatusCount.toLocaleString()}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Row 1: Search and Basic Filters */}
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Search</FilterLabel>
              <Input
                placeholder="Wallet ID, addresses, member name/email"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={() => handleFilterChange('search', search || null)}
                onBlur={() => handleFilterChange('search', search || null)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Company</FilterLabel>
              <Select
                placeholder="All companies"
                value={merchantFilter || undefined}
                onChange={(value) => {
                  setMerchantFilter(value);
                  handleFilterChange('merchantCompanyId', value || null);
                }}
                allowClear
                showSearch
                loading={loadingMerchants}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={merchants.map(m => ({ value: m.id, label: m.name }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Member ID</FilterLabel>
              <Input
                placeholder="External member ID"
                value={memberIdFilter}
                onChange={(e) => setMemberIdFilter(e.target.value)}
                onPressEnter={() => handleFilterChange('externalMemberId', memberIdFilter || null)}
                onBlur={() => handleFilterChange('externalMemberId', memberIdFilter || null)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Has Member</FilterLabel>
              <Select
                placeholder="All"
                value={hasMemberFilter || undefined}
                onChange={(value) => {
                  setHasMemberFilter(value);
                  handleFilterChange('hasMember', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="true">Has Member</Select.Option>
                <Select.Option value="false">No Member</Select.Option>
              </Select>
            </Col>
          </FilterRow>

          {/* Row 2: Status Filters */}
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Wallet Status</FilterLabel>
              <Select
                placeholder="All statuses"
                value={walletStatusFilter || undefined}
                onChange={(value) => {
                  setWalletStatusFilter(value);
                  handleFilterChange('walletStatus', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="ACTIVE">Active</Select.Option>
                <Select.Option value="PENDING">Pending</Select.Option>
                <Select.Option value="DELETED">Deleted</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Model Status</FilterLabel>
              <Select
                placeholder="All statuses"
                value={statusFilter || undefined}
                onChange={(value) => {
                  setStatusFilter(value);
                  handleFilterChange('status', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="ACTIVE">Active</Select.Option>
                <Select.Option value="PENDING">Pending</Select.Option>
                <Select.Option value="SUSPENDED">Suspended</Select.Option>
                <Select.Option value="DELETED">Deleted</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Notional Balance</FilterLabel>
              <Select
                placeholder="All balances"
                value={notionalBalanceFilter || undefined}
                onChange={(value) => {
                  setNotionalBalanceFilter(value);
                  handleFilterChange('notionalBalance', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="negative">Negative</Select.Option>
                <Select.Option value="positive">Positive</Select.Option>
                <Select.Option value="zero">Zero</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Under Watch</FilterLabel>
              <Select
                placeholder="All wallets"
                value={isUnderWatchFilter || undefined}
                onChange={(value) => {
                  setIsUnderWatchFilter(value);
                  handleFilterChange('isUnderWatch', value || null);
                }}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="true">Under Watch</Select.Option>
                <Select.Option value="false">Not Under Watch</Select.Option>
              </Select>
            </Col>
          </FilterRow>

          {/* Row 3: Date Filters */}
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Created Date</FilterLabel>
              <RangePicker
                value={createdDateRange}
                onChange={(dates) => handleDateRangeChange('created', dates)}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Updated Date</FilterLabel>
              <RangePicker
                value={updatedDateRange}
                onChange={(dates) => handleDateRangeChange('updated', dates)}
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                allowClear
              />
            </Col>
          </FilterRow>

          {/* Row 3: Clear Filters Button */}
          <FilterRow>
            <Col span={24}>
              <Button onClick={handleClearFilters} style={{ marginTop: 8 }}>
                Clear All Filters
              </Button>
            </Col>
          </FilterRow>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={wallets}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} wallets
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            className="dark-table"
            size="small"
          />
        </TableScrollWrapper>
      </Card>
    </PageContainer>
  );
};

export default Wallets;
